import {useEffect, type ReactNode} from "react";
import {FieldPath, useFieldArray, useFormContext} from "react-hook-form";
import {FormField, FormItem, FormMessage} from "@/components/ui/form.tsx";
import TextInputField from "@/components/forms/TextInputField.tsx";
import HiddenField from "@/components/forms/HiddenField.tsx";
import UseFieldArrayOptions from "@/components/forms/UseFieldArrayOptions.tsx";
import {Button} from "@/components/ui/button.tsx";
import {DungeonFormInput, DungeonFormOutput, newDungeonLevel} from "@/dungeons/admin/form/DungeonFormSchema.ts";
import {PlusIcon, Trash2} from "lucide-react";
import ImageUploadField from "@/images/ImageUploadField.tsx";
import {useTemporaryImageFormField} from "@/images/useTemporaryImageFormField.ts";

export default function DungeonBasicForm({registerCommit, registerUploading, onUploadStart}: {
    registerCommit: (levelId: string, commit?: () => void) => void;
    registerUploading: (levelId: string, uploading: boolean) => void;
    onUploadStart: () => void;
}) {
    const form = useFormContext<DungeonFormInput, unknown, DungeonFormOutput>();
    const {control} = form;
    const levels = useFieldArray({control, name: "levels"});
    const coverImage = useTemporaryImageFormField({
        form,
        name: "coverImage",
        onUploadStart,
    });

    useEffect(() => {
        registerCommit("cover-image", coverImage.commit);
        return () => registerCommit("cover-image");
    }, [coverImage.commit, registerCommit]);
    useEffect(() => {
        registerUploading("cover-image", coverImage.uploading);
        return () => registerUploading("cover-image", false);
    }, [coverImage.uploading, registerUploading]);

    return (
        <section className="space-y-5">
            <div className="grid gap-6 rounded-xl border border-border/80 bg-card/90 p-5 shadow-lg shadow-black/10 md:grid-cols-[minmax(0,1fr)_minmax(14rem,20rem)]">
                <div>
                    <TextInputField name="name" title="Name" placeholder="Dungeonname"/>
                    <p className="mt-2 text-sm text-muted-foreground">Das Titelbild erscheint auf der Dungeon-Karte. Ohne Titelbild wird die erste Level-Karte verwendet.</p>
                </div>
                <FormField control={control} name="coverImage" render={() => <FormItem>
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">Titelbild (optional)</span>
                        {coverImage.image && <Button type="button" variant="ghost" size="sm"
                            onClick={coverImage.clearImage} aria-label="Titelbild entfernen"><Trash2/>Entfernen</Button>}
                    </div>
                    <div className="aspect-video overflow-hidden rounded-lg border bg-surface">
                        <ImageUploadField image={coverImage.image} uploading={coverImage.uploading}
                            onFileSelected={coverImage.onFileSelected} alt="Dungeon-Titelbild" className="size-full"/>
                    </div>
                    <p className="text-xs text-muted-foreground">Empfohlenes Seitenverhältnis: 16:9</p>
                    {coverImage.image && coverImage.image.height > 0 &&
                        Math.abs(coverImage.image.width / coverImage.image.height - 16 / 9) > 0.1 &&
                        <p className="text-sm text-amber-400">Dieses Bild weicht von 16:9 ab und wird auf der Karte möglicherweise beschnitten.</p>}
                    <FormMessage/>
                </FormItem>}/>
            </div>
            <div className="flex items-center justify-between gap-3">
                <div><h2 className="text-lg font-semibold">Level-Karten</h2>
                    <p className="text-sm text-muted-foreground">Karten austauschen und Level benennen.</p></div>
                <Button type="button" size="sm" onClick={() => levels.append(newDungeonLevel(levels.fields.length) as never)}>
                    Level <PlusIcon/>
                </Button>
            </div>
            <FormField name="levels" render={() => (
                <FormItem className="gap-3">
                    <FormMessage/>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {levels.fields.map((field, index) => (
                            <DungeonLevelFields key={field.id}
                                                levelId={field.id}
                                                index={index}
                                                options={<UseFieldArrayOptions index={index} array={levels}/>}
                                                registerCommit={registerCommit}
                                                registerUploading={registerUploading}
                                                onUploadStart={onUploadStart}/>
                        ))}
                    </div>
                </FormItem>
            )}/>
        </section>
    );
}

function DungeonLevelFields({levelId, index, options, registerCommit, registerUploading, onUploadStart}: {
    levelId: string;
    index: number;
    options: ReactNode;
    registerCommit: (levelId: string, commit?: () => void) => void;
    registerUploading: (levelId: string, uploading: boolean) => void;
    onUploadStart: () => void;
}) {
    const form = useFormContext<DungeonFormInput, unknown, DungeonFormOutput>();
    const imageField = useTemporaryImageFormField({
        form,
        name: `levels.${index}.map.image` as FieldPath<DungeonFormInput>,
        onUploadStart,
    });

    useEffect(() => {
        registerCommit(levelId, imageField.commit);
        return () => registerCommit(levelId);
    }, [imageField.commit, levelId, registerCommit]);
    useEffect(() => {
        registerUploading(levelId, imageField.uploading);
        return () => registerUploading(levelId, false);
    }, [imageField.uploading, levelId, registerUploading]);

    return (
        <article className="overflow-hidden rounded-xl border border-border/80 bg-card/90 shadow-sm">
            <div className="flex items-start gap-2 border-b p-2">
                <HiddenField name={`levels.${index}.id`}/>
                <TextInputField name={`levels.${index}.name`} title={`Level ${index + 1}`} className="grow"/>
                {options}
            </div>
            <div className="h-36 bg-surface">
                <ImageUploadField image={imageField.image}
                                  uploading={imageField.uploading}
                                  onFileSelected={imageField.onFileSelected}
                                  alt={`Karte für Level ${index + 1}`}
                                  className="size-full"/>
            </div>
        </article>
    );
}
